import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '../types';
import { requestMagicLink, verifyMagicLink, getMe } from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  requestMagic: (email: string, turnstileToken: string) => Promise<void>;
  verifyMagic: (token: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await getMe();
        if (res.authenticated && res.email) {
          setUser({
            id: res.userId || 'me',
            email: res.email,
            name: res.email.split('@')[0],
            streak: 0,
            totalMinutes: 0,
            booksRead: 0,
          });
        }
      } catch (err) {
        // ignore
      } finally {
        setChecked(true);
      }
    };
    check();
  }, []);

  const requestMagic = async (email: string, turnstileToken: string) => {
    await requestMagicLink(email, turnstileToken);
  };

  const verifyMagic = async (token: string) => {
    const email = await verifyMagicLink(token);
    setUser((prev) => ({
      ...prev,
      id: prev?.id ?? crypto.randomUUID(),
      email,
      name: prev?.name ?? email.split('@')[0],
      streak: prev?.streak ?? 0,
      totalMinutes: prev?.totalMinutes ?? 0,
      booksRead: prev?.booksRead ?? 0,
    }));
  };

  const logout = () => {
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, requestMagic, verifyMagic, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within a AuthProvider');
  return context;
};
